import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {NewDatatable} from '../../../../components/NewDataTable/NewDataTable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import {Input} from '../../../../components/Input/Input'
import {ModalWrapper} from '../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../../hooks/useModals'
import {GET, POST, PUT} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import * as XLSX from 'sheetjs-style'

// Types
export enum StatusOrderService {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  CONCLUDED = 'completed',
  PROGRESS = 'in_progress',
}

interface SearchOrderServiceHotelDto {
  pluginspaceId?: number
  reservationId?: number
  statusHotelId?: number
  reservationDateStart?: string
  reservationDateEnd?: string
  postId?: number
}

interface StatusHotel {
  id: number
  pluginspaceId: number
  plugintypeId: number
  name: string
  slug: string
  order: number
  messageStatus: string
  status: boolean
  activeEmail: boolean
}

interface OrderServiceHotel {
  id: number
  idExt: string | null
  pluginspaceId: number
  postId: number
  statusHotelId: number | null
  statusHotel?: StatusHotel | null
  description?: string
  content: {
    service: {
      bookingId: number
      configIndex: number
      name: string
      isDirect: boolean
    }
    room: {
      roomNumber: string
      type: string
      price: number
    }
    metadata: {
      bookingType: string
      serviceTitle: string
      submittedAt: string
    }
  }
  price: number
  peopleQty: number
  reservationDate: string
  reservationTime: string | null
  reservationId: number
  created: string
  updated: string
  post: {
    id: number
    title: string
    summary: string | null
    status: boolean
    content: string
    img: string
    type: string
  }
  pluginspace: {
    id: number
    name: string
    corporateName: string
    subdomain: string
    type: string
  }
  hotelReservation: {
    id: number
    pluginspaceId: number
    roomId: number
    customerId: number
    checkIn: string
    checkOut: string | null
    status: string
  }
}

const OrdersReservationServiceContent: FC = () => {
  const {showWrapperModal, closeWrapperModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState<OrderServiceHotel[]>([])
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)
  const [filters, setFilters] = useState<SearchOrderServiceHotelDto>({})
  const [selectedOrder, setSelectedOrder] = useState<OrderServiceHotel | null>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [loadingStatusChange] = useState(false)
  const [newStatus, setNewStatus] = useState<string>('')
  const [modalType, setModalType] = useState<'details' | 'status' | null>(null)
  const [loadingExport, setLoadingExport] = useState(false)
  const [statusHotelOptions, setStatusHotelOptions] = useState<StatusHotel[]>([])

  const getStatusHotel = async () => {
    try {
      const res = await POST(`/status-hotel/filter`, {
        status: true,
      })
      const statusList = Array.isArray(res.data) ? res.data : []
      setStatusHotelOptions(statusList)
      return statusList
    } catch (e) {
      console.error(e)
      setStatusHotelOptions([])
      return []
    }
  }

  const getOrders = async (
    searchFilters?: SearchOrderServiceHotelDto,
    page: number = 1,
    take: number = 100,
    search: string = ''
  ) => {
    try {
      setLoading(true)

      const filtersToUse = searchFilters || filters

      // Constrói a URL com os parâmetros de paginação
      const queryParams = new URLSearchParams({
        page: page.toString(),
        take: take.toString(),
        ...(search && {search}),
      })

      const res = await POST(`/order-service-reservation-hotel/filter?${queryParams}`, filtersToUse)

      setOrders(res.data || [])
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('orders_room_service.error') as string,
        text: i18n.t('orders_room_service.error_loading_orders') as string,
      })
    }
  }

  const handleFilterChange = (field: keyof SearchOrderServiceHotelDto, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSearch = () => {
    getOrders(filters)
  }

  const handleClearFilters = () => {
    setFilters({})
    getOrders({})
  }

  const formatOrderData = (data: OrderServiceHotel[]) => {
    return data.map((item) => ({
      ...item,
      statusLabel:
        item.statusHotel?.name ||
        statusHotelOptions.find((status) => status.id === item.statusHotelId)?.name ||
        '-',
      serviceName: item.content?.service?.name || item.post?.title || '-',
      roomNumber: item.content?.room?.roomNumber || '-',
      serviceType: item.content?.service?.isDirect ? 'Direto' : 'Agendado',
      priceFormatted: new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(item.price / 100),
      dateFormatted: item.reservationDate || '-',
      hourFormatted: item.reservationTime ? item.reservationTime.substring(0, 5) : '-',
      createdFormatted: new Date(item.created).toLocaleString('pt-BR'),
    }))
  }

  const getStatusBadgeClass = (statusSlug?: string) => {
    const classes = {
      pending: 'bg-warning',
      confirmed: 'bg-success',
      canceled: 'bg-danger',
      cancelled: 'bg-danger',
      completed: 'bg-info',
      'in-progress': 'bg-primary',
      in_progress: 'bg-primary',
      attending: 'bg-primary',
      ready: 'bg-info',
    }
    return classes[(statusSlug || '') as keyof typeof classes] || 'bg-secondary'
  }

  const statusOptions = statusHotelOptions.map((status) => ({
    value: status.slug,
    select: status.name,
  }))

  const selectedStatusSlug =
    statusHotelOptions.find((option) => option.id === filters.statusHotelId)?.slug || ''

  // Função para buscar detalhes do pedido
  const getOrderDetails = async (orderId: number) => {
    try {
      // Abre o modal primeiro com loading
      setLoadingDetails(true)
      setModalType('details')
      showWrapperModal(i18n.t('orders_room_service.order_details') as string)

      const res = await GET(`/order-service-reservation-hotel/${orderId}`)
      setSelectedOrder(res.data)
      setLoadingDetails(false)
    } catch (e) {
      setLoadingDetails(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('orders_room_service.error') as string,
        text: i18n.t('orders_room_service.error_loading_details') as string,
      })
    }
  }

  // Função para alterar status
  const changeOrderStatus = async () => {
    if (!selectedOrder || !newStatus) {
      Swal.fire({
        icon: 'warning',
        title: i18n.t('orders_room_service.warning') as string,
        text: i18n.t('orders_room_service.select_status_warning') as string,
      })
      return
    }

    try {
      setLoading(true)

      await PUT(`/order-service-reservation-hotel/${selectedOrder.id}`, {
        statusHotelId: statusHotelOptions.find((status) => status.slug === newStatus)?.id,
        pluginspaceId: selectedOrder.pluginspaceId,
        reservationId: selectedOrder.reservationId,
      })

      Swal.fire({
        icon: 'success',
        title: i18n.t('orders_room_service.success') as string,
        text: i18n.t('orders_room_service.success_status_changed') as string,
      })

      // Atualiza a lista de pedidos
      getOrders(filters)

      // Fecha o modal
      closeWrapperModal()
      setNewStatus('')
      setSelectedOrder(null)
      setModalType(null)

      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('orders_room_service.error') as string,
        text: i18n.t('orders_room_service.error_changing_status') as string,
      })
    }
  }

  // Função para mostrar modal de alteração de status
  const showStatusModal = (order: OrderServiceHotel) => {
    setSelectedOrder(order)
    setNewStatus('')
    setModalType('status')

    showWrapperModal(i18n.t('orders_room_service.change_status_title') as string)
  }

  useEffect(() => {
    getOrders()
    getStatusHotel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exportToExcel = async () => {
    setLoadingExport(true)
    try {
      const dataToExport = formatOrderData(orders).map((item) => ({
        'ID Pedido': item.id,
        'ID Externo': item.idExt || '-',
        Serviço: item.serviceName,
        Quarto: item.roomNumber,
        Preço: item.priceFormatted,
        Pessoas: item.peopleQty,
        'ID Reserva': item.reservationId,
        Status: item.statusLabel,
        Data: item.dateFormatted,
        Hora: item.hourFormatted,
        'Criado Em': item.createdFormatted,
      }))

      const ws = XLSX.utils.json_to_sheet(dataToExport)

      const maxWidth = 50
      const colWidths = Object.keys(dataToExport[0] || {}).map((key) => ({
        wch: Math.min(Math.max(key.length, 12), maxWidth),
      }))
      ws['!cols'] = colWidths

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Pedidos')

      const timestamp = new Date().toISOString().split('T')[0]
      XLSX.writeFile(wb, `pedidos-reservationservice-${timestamp}.xlsx`)

      await Swal.fire({
        icon: 'success',
        title: String('Exportado com sucesso!'),
        text: String('O arquivo Excel foi baixado.'),
        confirmButtonText: String('OK'),
      })
    } catch (error) {
      console.error('Erro ao exportar:', error)
      await Swal.fire({
        icon: 'error',
        title: String('Erro ao exportar'),
        text: String('Ocorreu um erro ao gerar o arquivo Excel.'),
        confirmButtonText: String('OK'),
      })
    } finally {
      setLoadingExport(false)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('orders_room_service.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('orders_room_service.subtitle')}
                </span>
              </h3>
            </div>

            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.status')}
                    inputAttr={{
                      type: 'select',
                      placeholder: i18n.t('orders_room_service.select_status_placeholder'),
                    }}
                    value={selectedStatusSlug}
                    change={(value: string) =>
                      handleFilterChange(
                        'statusHotelId',
                        statusHotelOptions.find((option) => option.slug === value)?.id || undefined
                      )
                    }
                    options={[
                      {value: '', select: i18n.t('orders_room_service.all_status')},
                      ...statusOptions,
                    ]}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.start_date')}
                    inputAttr={{
                      type: 'date',
                      placeholder: i18n.t('orders_room_service.start_date_placeholder'),
                    }}
                    value={filters.reservationDateStart || ''}
                    change={(value: string) =>
                      handleFilterChange('reservationDateStart', value || undefined)
                    }
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.end_date')}
                    inputAttr={{
                      type: 'date',
                      placeholder: i18n.t('orders_room_service.end_date_placeholder'),
                    }}
                    value={filters.reservationDateEnd || ''}
                    change={(value: string) =>
                      handleFilterChange('reservationDateEnd', value || undefined)
                    }
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.reservation_id')}
                    inputAttr={{
                      type: 'number',
                    }}
                    value={filters.reservationId?.toString() || ''}
                    change={(value: string) =>
                      handleFilterChange('reservationId', value ? parseInt(value) : undefined)
                    }
                  />
                </div>

                <div className='col-12 d-flex justify-content-end'>
                  <div className='d-flex gap-2'>
                    <Button
                      text={i18n.t('orders_room_service.search')}
                      color='primary'
                      size='small'
                      click={handleSearch}
                    />
                    <Button
                      text={i18n.t('orders_room_service.clear_filters')}
                      color='danger'
                      size='small'
                      click={handleClearFilters}
                    />
                    <Button
                      text={'Exportar Excel'}
                      color='success'
                      size='small'
                      click={exportToExcel}
                      icon='fas fa-download'
                      loading={loadingExport}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='card-body'>
              <NewDatatable
                data={formatOrderData(orders)}
                headers={[
                  {
                    key: i18n.t('orders_room_service.service'),
                    value: 'serviceName',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.room'),
                    value: 'roomNumber',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.price'),
                    value: 'priceFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.people_qty'),
                    value: 'peopleQty',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.reservation'),
                    value: 'reservationId',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.status'),
                    value: 'statusLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.date'),
                    value: 'dateFormatted',
                    type: 'date',
                  },
                  {
                    key: i18n.t('orders_room_service.time'),
                    value: 'hourFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.actions'),
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-eye',
                    action: (item: any) => {
                      getOrderDetails(item.id)
                    },
                    title: i18n.t('orders_room_service.view_details'),
                  },
                  {
                    icon: 'fas fa-edit',
                    action: (item: any) => {
                      showStatusModal(item)
                    },
                    title: i18n.t('orders_room_service.change_status'),
                  },
                ]}
                color='primary'
                meta={meta}
                onChangeData={async (take: number, page: number, search: string, order?: any) => {
                  await getOrders(filters, page, take, search)
                }}
                search
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal para visualizar detalhes */}
      {modalType === 'details' && (
        <ModalWrapper
          confirmAction={() => {
            closeWrapperModal()
            setModalType(null)
            setSelectedOrder(null)
          }}
          textAction={i18n.t('orders_room_service.close')}
          noCloseButton={true}
          confirmButtomColor='danger'
          maxWidth={true}
          isFooterFixed={true}
        >
          {loadingDetails ? (
            <div className='text-center p-5'>
              <div
                className='spinner-border text-primary'
                role='status'
                style={{width: '3rem', height: '3rem'}}
              >
                <span className='sr-only'>{i18n.t('orders_room_service.loading')}</span>
              </div>
              <p className='mt-3 text-muted'>{i18n.t('orders_room_service.loading_details')}</p>
            </div>
          ) : selectedOrder ? (
            <div className='p-0'>
              {/* Hero Header */}
              <div className='p-4 my-4 border border-gray-300 rounded'>
                <div className='d-flex align-items-center justify-content-between flex-wrap'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-50px me-3'>
                      <div className='symbol-label bg-white'>
                        <i className='fas fa-concierge-bell text-primary fs-2'></i>
                      </div>
                    </div>
                    <div>
                      <h3 className='mb-1 fw-bold'>
                        {selectedOrder.content?.service?.name ||
                          selectedOrder.post?.title ||
                          i18n.t('orders_room_service.room_service')}
                      </h3>
                      {/* <div className='text-white-75'>
                        {i18n.t('orders_room_service.order')}:{' '}
                        <i className='fas fa-hashtag me-1'></i>
                        {selectedOrder.idExt}
                      </div> */}
                    </div>
                  </div>
                  <div className='text-start'>
                    <span
                      className={`badge fs-6 ${getStatusBadgeClass(
                        selectedOrder.statusHotel?.slug
                      )}`}
                    >
                      <i className='fas fa-circle me-1' style={{fontSize: '0.5rem'}}></i>
                      {selectedOrder.statusHotel?.name ||
                        statusHotelOptions.find(
                          (status) => status.id === selectedOrder.statusHotelId
                        )?.name ||
                        '-'}
                    </span>
                    {/* <div className='text-white-75 small mt-1'>
                      <i className='fas fa-clock me-1'></i>
                      {new Date(selectedOrder.created).toLocaleString('pt-BR')}
                    </div> */}
                  </div>
                </div>
              </div>

              {/* Content Body */}
              <div className='p-4'>
                {/* Quick Info Cards */}
                <div className='row g-3 mb-5'>
                  <div
                    className='col-md-4 cursor-pointer'
                    onClick={() =>
                      window.open(
                        `/hospitality/hotel-room/editar/${selectedOrder.hotelReservation.roomId}`,
                        '_blank'
                      )
                    }
                  >
                    <div className='card border-0 bg-light-success h-100'>
                      <div className='card-body text-center p-4'>
                        <i className='fas fa-door-open text-success fs-2x mb-2'></i>
                        <h6 className='fw-bold text-gray-800 mb-1'>
                          {i18n.t('orders_room_service.location')}
                        </h6>
                        <h4 className='text-success fw-bolder mb-0'>
                          {selectedOrder.content?.room?.roomNumber || 'N/A'}
                        </h4>
                        <small className='text-muted'>
                          {i18n.t('orders_room_service.room_number')}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-4'>
                    <div className='card border-0 bg-light-primary h-100'>
                      <div className='card-body text-center p-4'>
                        <i className='fas fa-calendar-check text-primary fs-2x mb-2'></i>
                        <h6 className='fw-bold text-gray-800 mb-1'>
                          {i18n.t('orders_room_service.scheduled_date')}
                        </h6>
                        <h4 className='text-primary fw-bolder mb-0'>
                          {selectedOrder.reservationDate
                            ? (() => {
                                const [year, month, day] = (
                                  selectedOrder.reservationDate as string
                                ).split('-')
                                return `${day}/${month}/${year}`
                              })()
                            : '_'}
                        </h4>
                        <small className='text-muted'>
                          {selectedOrder.reservationTime
                            ? `${selectedOrder.reservationTime.substring(0, 5)}`
                            : i18n.t('orders_room_service.no_time')}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div
                    className='col-md-4 cursor-pointer'
                    onClick={() =>
                      window.open(
                        `/hospitality/hotel-reservation/editar/${selectedOrder.reservationId}`,
                        '_blank'
                      )
                    }
                  >
                    <div className='card border-0 bg-light-info h-100'>
                      <div className='card-body text-center p-4'>
                        <i className='fas fa-user-tie text-info fs-2x mb-2'></i>
                        <h6 className='fw-bold text-gray-800 mb-1'>
                          {i18n.t('orders_room_service.reservation')}
                        </h6>
                        <h4 className='text-info fw-bolder mb-0'>#{selectedOrder.reservationId}</h4>
                        <small className='text-muted'>
                          {i18n.t('orders_room_service.reservation_id_label')}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Summary */}
                <div className='card border-0 shadow-sm mb-5'>
                  <div className='card-header bg-light border-0 py-3'>
                    <h5 className='card-title mb-0 fw-bold text-gray-800'>
                      <i className='fas fa-clipboard-list text-warning me-2'></i>
                      {i18n.t('orders_room_service.service_details')}
                    </h5>
                  </div>
                  <div className='card-body py-4'>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <div className='bg-light-info rounded p-3 border-start border-info border-5'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-tag text-info fs-2 me-3'></i>
                            <div>
                              <small className='text-muted fw-semibold'>
                                {i18n.t('orders_room_service.price')}
                              </small>
                              <p className='mb-0 fw-bold text-gray-800 fs-6'>
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(selectedOrder.price / 100)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <div className='bg-light-success rounded p-3 border-start border-success border-5'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-users text-success fs-2 me-3'></i>
                            <div>
                              <small className='text-muted fw-semibold'>
                                {i18n.t('orders_room_service.people_qty')}
                              </small>
                              <p className='mb-0 fw-bold text-gray-800 fs-6'>
                                {selectedOrder.peopleQty}{' '}
                                {selectedOrder.peopleQty === 1 ? 'pessoa' : 'pessoas'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {selectedOrder.description && (
                        <div className='col-md-12'>
                          <div className='bg-light-warning rounded p-3 border-start border-warning border-5'>
                            <div className='d-flex align-items-center'>
                              <i className='fas fa-align-left text-warning fs-2 me-3'></i>
                              <div>
                                <small className='text-muted fw-semibold'>
                                  {i18n.t('orders_room_service.descripition')}
                                </small>
                                <p className='mb-0 fw-bold text-gray-800 fs-6'>
                                  {selectedOrder.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </ModalWrapper>
      )}

      {/* Modal para alterar status */}
      {modalType === 'status' && selectedOrder && (
        <ModalWrapper
          confirmAction={changeOrderStatus}
          textAction='Alterar Status'
          noCloseButton={false}
          confirmButtomColor='primary'
          disableMainButton={loadingStatusChange || !newStatus}
          isFooterFixed={true}
          onCloseAction={() => {
            setModalType(null)
            setSelectedOrder(null)
            setNewStatus('')
          }}
        >
          <div className='p-3'>
            <div className='mb-4'>
              <h6 className='fw-bold'>
                {i18n.t('orders_room_service.order')} #{selectedOrder.id}
              </h6>
              <p className='mb-1'>
                <strong>{i18n.t('orders_room_service.service')}:</strong>{' '}
                {selectedOrder.content?.service?.name || selectedOrder.post?.title}
              </p>
              <p className='mb-1'>
                <strong>{i18n.t('orders_room_service.room')}:</strong>{' '}
                {selectedOrder.content?.room?.roomNumber || 'N/A'}
              </p>
              <p className='mb-1'>
                <strong>{i18n.t('orders_room_service.current_status')}:</strong>
                <span
                  className={`ms-2 badge ${getStatusBadgeClass(selectedOrder.statusHotel?.slug)}`}
                >
                  {selectedOrder.statusHotel?.name ||
                    statusHotelOptions.find((status) => status.id === selectedOrder.statusHotelId)
                      ?.name ||
                    '-'}
                </span>
              </p>
            </div>

            <Input
              label={i18n.t('orders_room_service.new_status')}
              inputAttr={{
                type: 'select',
                placeholder: i18n.t('orders_room_service.new_status_placeholder'),
                required: true,
              }}
              value={newStatus}
              change={(value: string) => setNewStatus(value)}
              options={[
                {value: '', select: i18n.t('orders_room_service.select_status')},
                ...statusOptions,
              ]}
              required={true}
            />
          </div>
        </ModalWrapper>
      )}
    </>
  )
}

const OrdersReservationService: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {
            title: i18n.t('orders_room_service.title'),
            path: 'orders-room-service',
            isActive: true,
          },
        ]}
      >
        {i18n.t('orders_room_service.title')}
      </PageTitle>
      <OrdersReservationServiceContent />
    </>
  )
}

export {OrdersReservationService}
